import {Fragment} from 'react';
import {Link} from 'react-router-dom';
import {DefaultMetaTags} from '@common/seo/default-meta-tags';
import {SiteHeader} from '@common/ui/navigation/site-header';
import {Footer} from '@common/ui/footer/footer';

const privacyRows = [
  {
    title: (
      <>
        We <span className="text-[#d8562a]">don&apos;t</span> show you ads
      </>
    ),
    points: [
      {
        text: `Not on free tiers, not with paid tiers. Our business model is simple: people pay us for storage and sending files, nothing more, nothing less. You're a customer, not a product.`,
        imageSrc: '/images/Page_privacy/Not_on_Free_%26%20Paid_Tiers.png',
        imageAlt: 'Not on free and paid tiers',
      },
      {
        text: `In a world where advertisements seem to be in many websites, why not try a different route?`,
        imageSrc: '/images/Page_privacy/Why_not_Try_a_Different_Route.png',
        imageAlt: 'Why not try a different route',
      },
    ],
    cardFirst: true,
  },
  {
    title: (
      <>
        We <span className="text-[#d8562a]">don&apos;t</span> sell your data
      </>
    ),
    points: [
      {
        text: `Not to advertisers, not to data brokers, not to "trusted partners." Your file metadata, your usage patterns, your shared lists - none for sale, none will be.`,
        imageSrc: '/images/Page_privacy/We_don’t_Sell_Your_Data.png',
        imageAlt: `We don't sell your data`,
      },
    ],
    cardFirst: false,
  },
  {
    title: (
      <>
        <span className="text-[#d8562a]">No</span> AI integration
      </>
    ),
    points: [
      {
        text: `Your documents, photos, and videos are not training data. We don't scan them to build models, and we don't pipe them to third-party AI services. What you upload stays what you uploaded.`,
        imageSrc: '/images/Page_privacy/We_don’t_Pipe_Them.png',
        imageAlt: `We don't pipe them to third-party AI services`,
      },
      {
        text: (
          <>
            Cloud storage has quietly become an AI data pipeline. Files get
            scanned for &quot;smart search.&quot; Documents get summarized by
            assistants you didn&apos;t ask for. Photos get analyzed for
            &quot;memories.&quot; And somewhere in the terms of service, your
            content becomes training data.
            <br />
            <br />
            We don&apos;t do any of that.
          </>
        ),
        imageSrc:
          '/images/Page_privacy/Cloud_Storage_has_Quietly_Becoma_an_AI_Data_PipeLine.png',
        imageAlt: 'Cloud storage has quietly become an AI data pipeline',
      },
    ],
    cardFirst: true,
  },
  {
    title: (
      <>
        We <span className="text-[#d8562a]">don&apos;t</span> profile you
      </>
    ),
    points: [
      {
        text: `We don't build profiles. We don't track your behavior. We don't analyze your files to learn about you. We don't correlate your activity with anything outside our service. Want us to know even less? Turn on E2EE in our higher paid tiers and we can't see your files at all.`,
        imageSrc: '/images/Page_privacy/We_don’t_Profile_You.png',
        imageAlt: `We don't profile you`,
      },
    ],
    cardFirst: false,
  },
];

const privacyHeroImage = {
  src: '/images/Page_privacy/Your_Files_are_Yours.png',
  alt: 'Your files are yours',
} as const;

const IMAGE_FRAME_CLASS =
  'mx-auto h-[200px] w-[92%] object-contain object-center lg:h-[430px] lg:w-[90%]';

const IMAGE_WRAP_CLASS = 'w-full';

export function PrivacyPage() {
  return (
    <Fragment>
      <DefaultMetaTags />
      <main className="min-h-screen bg-[#f5f5f7] text-[#1a1a1a] dark:bg-[#14161a] dark:text-[#f3f4f6] ">
        <SiteHeader active="features" />
        <div className="relative mx-auto w-full max-w-[1895px] px-[100px] pb-[24px] lg:pb-[25px] max-[767px]:px-[50px]">
          <section className="mx-auto mt-[56px] grid w-full max-w-[1565px] grid-cols-1 gap-[28px] lg:mt-[170px] lg:gap-[34px] lg:grid-cols-[minmax(0,1fr)_638px] lg:items-start min-[1024px]:max-[1386px]:grid-cols-[minmax(0,1fr)_400px]">
            <div>
              <h1 className="max-w-[649px] font-['Inter',sans-serif] text-[34px] font-bold leading-[1.1] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[38px] lg:font-['JetBrains_Mono',monospace] lg:text-[60px] lg:leading-[1.08]">
                Your files are yours.
                <br />
                We mean that literally.
              </h1>
              <p className="mt-[18px] max-w-[649px] text-[18px] font-bold leading-[1.36] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:mt-[30px] lg:text-[25px] lg:leading-[1.32]">
                <span className="italic text-[#d8562a]">No</span> ads.
                <br />
                <span className="italic text-[#d8562a]">No</span> selling your data.
                <br />
                <span className="italic text-[#d8562a]">No</span> AI built-in.
                <br />
                <br />
                <span className="italic text-[#d8562a]">No</span> tracking pixels,
                <span className="italic text-[#d8562a]"> no</span> behavioral
                profiling, <span className="italic text-[#d8562a]">no</span>{' '}
                &quot;anonymized&quot; data brokered to the highest bidder.
                <br />
                <br />
                It&apos;s a lot of <span className="italic text-[#d8562a]">no</span>,
                but because we&apos;re building for a simple world.
              </p>
            </div>
            <div className="flex flex-col items-center gap-[18px] lg:gap-[28px]">
              <div className={IMAGE_WRAP_CLASS}>
                <img
                  src={privacyHeroImage.src}
                  alt={privacyHeroImage.alt}
                  className={IMAGE_FRAME_CLASS}
                />
              </div>
              <Link
                to="/register"
                className="inline-flex h-[60px] w-[141px] items-center justify-center rounded-[24px] bg-[#2b7e20] text-[17px] font-medium text-white transition-colors hover:bg-[#3c7d44]"
              >
                Try for free
              </Link>
            </div>
          </section>

          <div className="mx-auto mt-[72px] grid w-full max-w-[1565px] grid-cols-1 gap-y-[80px] lg:mt-[110px] lg:gap-y-[132px]">
            {privacyRows.map((section, sectionIndex) => (
              <section key={sectionIndex}>
                <h2
                  className={`font-['Inter',sans-serif] text-[32px] font-bold leading-[1.15] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[36px] lg:text-[60px] lg:leading-[1.1] ${
                    section.cardFirst ? 'text-left lg:text-right' : 'text-left'
                  }`}
                >
                  {section.title}
                </h2>

                <div className="mt-[20px] grid gap-y-[36px] lg:mt-[30px] lg:gap-y-[48px]">
                  {section.points.map((point, index) =>
                    point.imageSrc ? (
                      <div
                        key={point.imageAlt || index}
                        className="grid grid-cols-1 items-center gap-[24px] lg:grid-cols-2 lg:gap-[36px]"
                      >
                        <div
                          className={`${
                            (section.cardFirst && index % 2 === 0) ||
                            (!section.cardFirst && index % 2 !== 0)
                              ? 'lg:order-1'
                              : 'lg:order-2'
                          } ${IMAGE_WRAP_CLASS}`}
                        >
                          <img
                            src={point.imageSrc}
                            alt={point.imageAlt}
                            className={IMAGE_FRAME_CLASS}
                          />
                        </div>
                        <p
                          className={`${
                            (section.cardFirst && index % 2 === 0) ||
                            (!section.cardFirst && index % 2 !== 0)
                              ? 'lg:order-2'
                              : 'lg:order-1'
                          } max-w-[631px] text-[18px] font-bold leading-[1.38] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:text-[25px] lg:leading-[1.35]`}
                        >
                          {point.text}
                        </p>
                      </div>
                    ) : (
                      <p
                        key={index}
                        className="text-[18px] font-bold leading-[1.38] text-[#6a7480] dark:text-[#b3bcc8] sm:text-[20px] lg:text-[25px] lg:leading-[1.35]"
                      >
                        {point.text}
                      </p>
                    ),
                  )}
                </div>
              </section>
            ))}
          </div>

          <section className="mx-auto mt-[84px] w-full max-w-[1565px] text-center lg:mt-[130px]">
            <h2 className="font-['Inter',sans-serif] text-[36px] font-bold leading-[1.2] text-[#1a1a1a] dark:text-[#f3f4f6] sm:text-[42px] lg:text-[60px] lg:leading-[1.16]">
              Want to give it a try?
            </h2>
            <p className="mx-auto mt-[14px] max-w-[629px] text-[18px] font-normal leading-[1.4] text-[#1a1a1a] dark:text-[#e4e8ef] sm:text-[22px] lg:mt-[20px] lg:text-[30px] lg:leading-[1.35]">
              Whether you choose a free account, paid or just passing by, we have
              something for you.
            </p>
            <Link
              to="/register"
              className="mt-[22px] inline-flex h-[64px] items-center justify-center rounded-[30px] bg-[#2b7e20] px-[30px] text-[21px] font-medium text-white transition-colors hover:bg-[#3c7d44] lg:mt-[32px] lg:h-[108px] lg:rounded-[43px] lg:px-[44px] lg:text-[30px]"
            >
              Try for free
            </Link>
          </section>

          <Footer className="mt-[44px] lg:mt-[120px] lg:min-h-[190px]" />
        </div>
      </main>
    </Fragment>
  );
}
